// src/components/MachineStatistics/MachineStatisticsPage.js
import React, { useEffect, useMemo, useState } from "react";
import { FaPrint } from 'react-icons/fa';
import Navbar from "../Navbar/Navbar";
import api from "../Axios/axios";
import "./MachineStatisticsPage.scss";

const MIN_ANALYSIS_YEAR = 2025;

const ayIsimleri = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Yardımcı: string veya Date -> Date (geçersizse null)
const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const startOfMonth = (year, monthIndex) => new Date(year, monthIndex, 1);
const endOfMonth = (year, monthIndex) =>
  new Date(year, monthIndex + 1, 0, 23, 59, 59);

// Kiralamanın belli bir ayla çakışıp çakışmadığı (ham rental objesi için)
const rentalOverlapsMonth = (rental, year, monthIndex) => {
  const start = toDate(rental.startDate || rental.rentalStartDate);
  const end = toDate(rental.endDate || rental.rentalEndDate) || new Date();
  if (!start) return false;

  const mStart = startOfMonth(year, monthIndex);
  const mEnd = endOfMonth(year, monthIndex);

  return start <= mEnd && end >= mStart;
};

// Rental içinden kule vinç Id
const getRentalCraneId = (r) =>
  r.towerCraneId || r.towerCraneID || r.towerCraneIdFk || null;

// Rental içinden kule vinç seri/kod
const getRentalCraneSerial = (r) =>
  r.towerCraneSerialNo || r.towerCraneCode || r.serialNo || "";

const MachineStatisticsPage = () => {
  const [rentals, setRentals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [towerCranes, setTowerCranes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const now = new Date();
  const nowYear = now.getFullYear();
  const initialYear = Math.max(nowYear, MIN_ANALYSIS_YEAR);
  const [selectedYear, setSelectedYear] = useState(initialYear);

  // Makine sahibi firma filtresi (owner)
  const [selectedOwnerId, setSelectedOwnerId] = useState(""); // "" = tüm sahipler

  // -------- DATA FETCH --------
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError("");
      try {
        const [rentalRes, companyRes, craneRes] = await Promise.all([
          api.get("/Rental"),
          api.get("/Company"),
          api.get("/TowerCrane"),
        ]);

        const rentalList = rentalRes?.data?.data ?? rentalRes?.data ?? [];
        const companyList = companyRes?.data?.data ?? companyRes?.data ?? [];
        const craneList = craneRes?.data?.data ?? craneRes?.data ?? [];

        setRentals(Array.isArray(rentalList) ? rentalList : []);
        setCompanies(Array.isArray(companyList) ? companyList : []);
        setTowerCranes(Array.isArray(craneList) ? craneList : []);
      } catch (err) {
        console.error(err);
        setError("İstatistik verileri çekilirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Makine sahibi firmalar (hasTowerCrane === true)
  const craneOwners = useMemo(
    () => companies.filter((c) => c.hasTowerCrane),
    [companies]
  );

  // Yıl seçenekleri: min 2025, max = currentYear + 1 (2025'teysek 2026 dahil, 2026'dayken 2027 vs.)
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const maxYear = current + 1;
    const years = [];
    for (let y = maxYear; y >= MIN_ANALYSIS_YEAR; y--) {
      years.push(y);
    }
    return years;
  }, []);

  const getOwnerName = (id) => {
    if (!id) return "Tüm Makine Sahipleri";
    const c = craneOwners.find((x) => String(x.id) === String(id));
    return c?.name || "Bilinmeyen Firma";
  };

  // Kule vinçleri id -> crane map
  const craneMap = useMemo(() => {
    const map = new Map();
    towerCranes.forEach((c) => {
      if (c && c.id != null) map.set(String(c.id), c);
    });
    return map;
  }, [towerCranes]);

  const yearNum = Number(selectedYear);

  // -------- YILA / OWNER'A GÖRE FİLTRELİ RENTAL LİSTESİ --------
  const filteredRentals = useMemo(() => {
    const yearStart = new Date(yearNum, 0, 1, 0, 0, 0);
    const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59);

    return rentals.filter((r) => {
      const start = toDate(r.startDate || r.rentalStartDate);
      const end = toDate(r.endDate || r.rentalEndDate) || new Date();
      if (!start) return false;

      // Seçilen yılla çakışıyor mu?
      const overlapsYear = !(start > yearEnd || end < yearStart);
      if (!overlapsYear) return false;

      // Makine sahibi filtresi
      const craneId = getRentalCraneId(r);
      const crane = craneId != null ? craneMap.get(String(craneId)) : null;

      if (selectedOwnerId) {
        if (!crane) return false;
        if (String(crane.ownerId) !== String(selectedOwnerId)) return false;
      }

      return true;
    });
  }, [rentals, yearNum, selectedOwnerId, craneMap]);

  // -------- AYLIK BAŞLANGIÇ / BİTİŞ SAYILARI --------
  const { monthlyStarts, monthlyEnds } = useMemo(() => {
    const starts = Array(12).fill(0);
    const ends = Array(12).fill(0);

    filteredRentals.forEach((r) => {
      const s = toDate(r.startDate || r.rentalStartDate);
      const e = toDate(r.endDate || r.rentalEndDate);

      if (s && s.getFullYear() === yearNum) starts[s.getMonth()] += 1;
      if (e && e.getFullYear() === yearNum) ends[e.getMonth()] += 1;
    });

    return { monthlyStarts: starts, monthlyEnds: ends };
  }, [filteredRentals, yearNum]);

  // -------- AYLIK AKTİF MAKİNE SAYISI --------
  const monthlyActiveMachines = useMemo(() => {
    const result = Array(12).fill(0);

    for (let m = 0; m < 12; m++) {
      const set = new Set();
      filteredRentals.forEach((r) => {
        if (rentalOverlapsMonth(r, yearNum, m)) {
          const craneId = getRentalCraneId(r);
          if (craneId != null) set.add(craneId);
        }
      });
      result[m] = set.size;
    }

    return result;
  }, [filteredRentals, yearNum]);

  // -------- MAKİNE BAZLI ÖZET + BOŞ GÜN (KİRAYA GİTMEYENLER DAHİL) --------
  const machineSummaries = useMemo(() => {
    const yearStart = new Date(yearNum, 0, 1, 0, 0, 0);
    const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59);
    const today = new Date();
    const todayYear = today.getFullYear();

    // 1) Bu yıl ile çakışan kiralamaları, makine bazında grupla
    const rentalsByCrane = new Map();

    filteredRentals.forEach((r) => {
      const craneId = getRentalCraneId(r);
      if (!craneId) return;

      const start = toDate(r.startDate || r.rentalStartDate);
      const end = toDate(r.endDate || r.rentalEndDate) || new Date();
      if (!start) return;

      const key = String(craneId);
      let arr = rentalsByCrane.get(key);
      if (!arr) {
        arr = [];
        rentalsByCrane.set(key, arr);
      }
      arr.push({ start, end });
    });

    // 2) Owner filtresinden geçen TÜM kule vinçleri al
    const cranesForOwner = towerCranes.filter((crane) => {
      if (!crane || crane.id == null) return false;

      const creation = toDate(crane.creationDate);

      // Makine henüz bu yılda sistemde değilse (daha sonra oluşturulmuş) bu yıl için gösterme
      if (creation && creation.getFullYear() > yearNum) {
        return false;
      }

      if (selectedOwnerId) {
        if (String(crane.ownerId) !== String(selectedOwnerId)) return false;
      }

      return true;
    });

    const summaries = [];

    cranesForOwner.forEach((crane) => {
      const craneIdStr = String(crane.id);
      const rentalsForCrane = rentalsByCrane.get(craneIdStr) || [];

      // Bu yıl ile çakışan kiralamalar
      const yearRentals = rentalsForCrane.filter(
        (r) => r.start <= yearEnd && r.end >= yearStart
      );

      // --- Bu Yıl Tahmini Çalışma Günü (tüm yıl için, geçmiş+gelecek) ---
      let totalDaysYear = 0;
      let firstStart = null;
      let lastEnd = null;

      yearRentals.forEach((r) => {
        const effStart = r.start < yearStart ? yearStart : r.start;
        const effEnd = r.end > yearEnd ? yearEnd : r.end;

        if (effEnd > effStart) {
          const diffMs = effEnd.getTime() - effStart.getTime();
          const days = Math.max(0, Math.round(diffMs / MS_PER_DAY));
          totalDaysYear += days;
        }

        if (!firstStart || r.start < firstStart) firstStart = r.start;
        if (!lastEnd || r.end > lastEnd) lastEnd = r.end;
      });

      // --- BOŞ GÜN HESABI (yalnızca gerçekleşen dönem + creation sonrası) ---
      const craneCreation = toDate(crane.creationDate);

      // Makinenin bu yıl için "aktif olabileceği" başlangıç:
      let activeStart = yearStart;
      if (
        craneCreation &&
        craneCreation.getFullYear() === yearNum &&
        craneCreation > yearStart
      ) {
        activeStart = craneCreation;
      } else if (craneCreation && craneCreation < yearStart) {
        activeStart = yearStart;
      }

      // Gözlem bitiş tarihi:
      // - Geçmiş yıllar: yıl sonu
      // - Bu yıl: bugün
      // - Gelecek yıllar: henüz gerçekleşmedi -> boş gün sayma
      let observeEnd = yearEnd;
      if (yearNum < todayYear) {
        observeEnd = yearEnd;
      } else if (yearNum === todayYear) {
        observeEnd = today < yearEnd ? today : yearEnd;
      } else {
        // yearNum > todayYear → tamamen gelecek yıl
        observeEnd = activeStart; // süre 0, boş gün = 0
      }

      let emptyDays = 0;

      if (observeEnd > activeStart) {
        const observedTotalDays = Math.max(
          0,
          Math.round((observeEnd.getTime() - activeStart.getTime()) / MS_PER_DAY)
        );

        // Gözlenen dönemdeki dolu günler
        let observedBusyDays = 0;
        yearRentals.forEach((r) => {
          const s = r.start;
          const e = r.end;
          const effStart =
            s < activeStart ? activeStart : s;
          const effEnd =
            e > observeEnd ? observeEnd : e;

          if (effEnd > effStart) {
            const diffMs = effEnd.getTime() - effStart.getTime();
            const days = Math.max(0, Math.round(diffMs / MS_PER_DAY));
            observedBusyDays += days;
          }
        });

        emptyDays = Math.max(0, observedTotalDays - observedBusyDays);
      }

      summaries.push({
        towerCraneId: crane.id,
        serialNo: crane.serialNo || crane.code || "",
        rentalCount: yearRentals.length,
        totalDays: totalDaysYear,
        emptyDays,
        firstStart,
        lastEnd,
      });
    });

    // Önce kiralama sayısına göre, eşitse seri/koda göre sırala
    summaries.sort((a, b) => {
      if (b.rentalCount !== a.rentalCount) {
        return b.rentalCount - a.rentalCount;
      }
      return String(a.serialNo || "").localeCompare(String(b.serialNo || ""));
    });

    return summaries;
  }, [filteredRentals, yearNum, towerCranes, selectedOwnerId]);

  // -------- ÖZET KARTLAR --------
  const totalMachines = useMemo(() => {
    // Owner filtresine uyan tüm vinç sayısı (tablodaki ile uyumlu olsun)
    const cranesForOwner = towerCranes.filter((crane) => {
      if (!crane || crane.id == null) return false;
      const creation = toDate(crane.creationDate);
      if (creation && creation.getFullYear() > yearNum) return false;
      if (selectedOwnerId) {
        if (String(crane.ownerId) !== String(selectedOwnerId)) return false;
      }
      return true;
    });
    return cranesForOwner.length;
  }, [towerCranes, selectedOwnerId, yearNum]);

  const totalRentals = filteredRentals.length;

  const totalEmptyDays = useMemo(
    () => machineSummaries.reduce((sum, m) => sum + m.emptyDays, 0),
    [machineSummaries]
  );

  const totalEmptyMonthsApprox = useMemo(
    () => Math.round(totalEmptyDays / 30),
    [totalEmptyDays]
  );

  // Yazdırma fonksiyonu
  const handlePrint = () => {
    window.print();
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("tr-TR");
  };

  // -------- RENDER --------
  return (
    <>
      <Navbar />
      <div className="machine-stats-page">
        <div className="machine-stats-page__content">
          {/* Üst başlık + YIL filtresi */}
          <div className="machine-stats-page__header">
            <div>
              <h1 className="machine-stats-page__title">Kule Vinç İstatistikleri</h1>
              <div className="machine-stats-page__subtitle">
                {getOwnerName(selectedOwnerId)} – {selectedYear}
              </div>
            </div>

            <div className="machine-stats-page__filters">
              <button className="machine-stats-page__print-btn" onClick={handlePrint} title="Yazdır">
                <FaPrint /> Yazdır
              </button>
              <div className="machine-stats-page__filter">
                <label>Yıl</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {error && <div className="machine-stats-page__error">{error}</div>}
          {loading && (
            <div className="machine-stats-page__info">Veriler yükleniyor...</div>
          )}

          {/* Özet Kartlar */}
          <div className="machine-stats-page__cards">
            <div className="machine-stats-page__card">
              <div className="machine-stats-page__card-label">
                Seçilen Yılda Sistemede Bulunan Makine
              </div>
              <div className="machine-stats-page__card-value">
                {totalMachines}
              </div>
            </div>

            <div className="machine-stats-page__card">
              <div className="machine-stats-page__card-label">
                Seçilen Yılda Toplam Kiralama
              </div>
              <div className="machine-stats-page__card-value">
                {totalRentals}
              </div>
            </div>

            <div className="machine-stats-page__card">
              <div className="machine-stats-page__card-label">
                Toplam Boş Ay (Makine x Ay)
              </div>
              <div className="machine-stats-page__card-value">
                {totalEmptyMonthsApprox}
              </div>
            </div>

          </div>

          {/* Grafikler */}
          <div className="machine-stats-page__charts">
            {/* Aylık kiralama başlangıç sayısı */}
            <div className="machine-stats-page__chart">
              <h2 className="machine-stats-page__chart-title">
                Aylara Göre Kiralama Başlangıç Sayısı
              </h2>
              <div className="machine-stats-page__bars">
                {monthlyStarts.map((val, idx) => {
                  const max = Math.max(...monthlyStarts, 1);
                  const width = (val / max) * 100;
                  return (
                    <div key={idx} className="machine-stats-page__bar-row">
                      <div className="machine-stats-page__bar-label">
                        {ayIsimleri[idx]}
                      </div>
                      <div className="machine-stats-page__bar-track">
                        <div
                          className="machine-stats-page__bar-fill"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <div className="machine-stats-page__bar-value">
                        {val}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Aylık kiralama bitiş sayısı */}
            <div className="machine-stats-page__chart">
              <h2 className="machine-stats-page__chart-title">
                Aylara Göre Kiralama Bitiş Sayısı
              </h2>
              <div className="machine-stats-page__bars">
                {monthlyEnds.map((val, idx) => {
                  const max = Math.max(...monthlyEnds, 1);
                  const width = (val / max) * 100;
                  return (
                    <div key={idx} className="machine-stats-page__bar-row">
                      <div className="machine-stats-page__bar-label">
                        {ayIsimleri[idx]}
                      </div>
                      <div className="machine-stats-page__bar-track">
                        <div
                          className="machine-stats-page__bar-fill machine-stats-page__bar-fill--secondary"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <div className="machine-stats-page__bar-value">
                        {val}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Aylara göre aktif makine sayısı */}
            <div className="machine-stats-page__chart">
              <h2 className="machine-stats-page__chart-title">
                Aylara Göre Çalışan Makine Sayısı (Benzersiz)
              </h2>
              <div className="machine-stats-page__bars">
                {monthlyActiveMachines.map((val, idx) => {
                  const max = Math.max(...monthlyActiveMachines, 1);
                  const width = (val / max) * 100;
                  return (
                    <div key={idx} className="machine-stats-page__bar-row">
                      <div className="machine-stats-page__bar-label">
                        {ayIsimleri[idx]}
                      </div>
                      <div className="machine-stats-page__bar-track">
                        <div
                          className="machine-stats-page__bar-fill machine-stats-page__bar-fill--third"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <div className="machine-stats-page__bar-value">
                        {val}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Makine Bazlı Tablo + MAKİNE SAHİBİ FİLTRESİ */}
          <div className="machine-stats-page__table-wrapper">
            <div className="machine-stats-page__table-header-row">
              <h2 className="machine-stats-page__section-title">
                Makine Bazlı İstatistikler ({getOwnerName(selectedOwnerId)} /{" "}
                {selectedYear})
              </h2>

              <div className="machine-stats-page__filter machine-stats-page__filter--owner">
                <label>Makine Sahibi Firma</label>
                <select
                  value={selectedOwnerId}
                  onChange={(e) => setSelectedOwnerId(e.target.value)}
                >
                  <option value="">Tüm Makine Sahipleri</option>
                  {craneOwners.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="machine-stats-page__summary-line">
              Bu yıl toplam <strong>{machineSummaries.length}</strong> makine için
              yaklaşık <strong>{totalEmptyDays}</strong> gün
              {totalEmptyDays > 0 && (
                <>
                  {" "}
                  (~<strong>{totalEmptyMonthsApprox}</strong> ay)
                </>
              )}{" "}
              boş kalma süresi (makine x gün) hesaplandı.
            </div>

            {machineSummaries.length === 0 ? (
              <div className="machine-stats-page__info">
                Seçilen yıl / makine sahibi için istatistik bulunamadı.
              </div>
            ) : (
              <table className="machine-stats-page__table">
                <thead>
                  <tr>
                    <th>Makine (Seri / Kod)</th>
                    <th>Bu Yıl Kiralama Adedi</th>
                    <th>Bu Yıl Tahmini Çalışma Günü</th>
                    <th>Bu Yıl Boş Gün Sayısı</th>
                    <th>İlk Kiralama Tarihi</th>
                    <th>Son Kiralama Bitiş Tarihi</th>
                  </tr>
                </thead>
                <tbody>
                  {machineSummaries.map((m) => (
                    <tr key={m.towerCraneId}>
                      <td>{m.serialNo || `#${m.towerCraneId}`}</td>
                      <td>{m.rentalCount}</td>
                      <td>{m.totalDays}</td>
                      <td>{m.emptyDays}</td>
                      <td>{formatDate(m.firstStart)}</td>
                      <td>{formatDate(m.lastEnd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MachineStatisticsPage;
